import React, { useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import { FieldInputProps, FieldMetaProps, FormikContextType, useField, useFormikContext } from 'formik';
import { ActionMeta, GroupBase, OptionsOrGroups } from 'react-select';
import isNil from 'lodash/isNil';
import { ISelectFieldProps } from '../SelectField/SelectField';
import { mergeTests } from '../../test-utilities/mergeTests';
import { ISelectOption, ISelectProps } from './Select';
import { extractFormCellProps, FormCell } from '../FormCell';
import { defaultSelectStyles } from './Select.styles';
import { ErrorBoundaryConstellation } from '../../../constellationExtended/ErrorBoundary/ErrorBoundaryConstellation';

interface ICreatableProps extends Omit<ISelectProps, 'isMulti'> {
  isValidNewOption?: (inputValue: string, value: ReadonlyArray<any>, options: ReadonlyArray<any>) => boolean;
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
}

export const Creatable: React.FC<ICreatableProps> = (props) => {
  const { formCellProps, inputProps } = extractFormCellProps(props);
  const styles = useMemo(() => ({ ...defaultSelectStyles, ...inputProps.styles }), [inputProps.styles]);

  return (
    <ErrorBoundaryConstellation>
      <FormCell {...formCellProps} disabled={inputProps.isDisabled} data-test-id={props['data-test-id']}>
        <CreatableSelect
          {...inputProps}
          styles={styles}
          isDisabled={inputProps.isDisabled}
          className={'select'}
          classNamePrefix={'select'}
        />
      </FormCell>
    </ErrorBoundaryConstellation>
  );
};

const getErrorText = (context: FormikContextType<unknown>, fieldMetaProps: FieldMetaProps<unknown>) => {
  if (context.submitCount === 0) {
    return null;
  }
  return fieldMetaProps.error ?? null;
};

const getValue = (field: FieldInputProps<any>, options: OptionsOrGroups<any, GroupBase<any>>) => {
  const formValue = field.value;
  if (!formValue) {
    return null;
  }
  const existingOptionVal = options.find((o) => o.value === formValue);
  if (existingOptionVal) {
    return existingOptionVal;
  }
  return { label: formValue, value: formValue };
};

export const CreatableSelectField: React.FC<ISelectFieldProps> = ({ name, options = [], ...rest }) => {
  const formik = useFormikContext();
  const [field, meta, helpers] = useField<any>({ name });

  const onChange = (value: any, actionMeta: ActionMeta<ISelectOption>) => {
    helpers.setTouched(true);
    helpers.setValue(value ? value.value : undefined);
    if (rest.onChange) {
      rest.onChange(value, actionMeta);
    }
  };
  const errorText = getErrorText(formik, meta);

  const value = getValue(field, options);

  return (
    <Creatable
      {...rest}
      options={options}
      data-test-id={mergeTests(rest['data-test-id'], 'creatable-input')}
      onChange={onChange}
      errorText={isNil(rest.errorText) ? errorText : rest.errorText}
      value={value}
    />
  );
};
